import { ActivityEntityName } from '../../../../entities/shared/activity.entity.name';
import { CustomFieldType } from '../../../../entities/fields/custom.field.type';
import { MetadataEntityFieldData, MetadataLookupTypeData } from '../model/metadata.entity.field.data';
import { MetadataEntityFieldType } from '../model/metadata.entity.field.types';
import { getMetadataEntityFieldType } from './get.entity.field.type';

/**
 * How big a field's value set is, and therefore whether a UI may enumerate it.
 *
 * - `static` — the whole set lives in metadata (enum options, a boolean).
 * - `bounded-lookup` — a reference list a tenant configures and keeps short (statuses, priorities,
 *   job types, teams). One picker call returns the whole thing.
 * - `open` — unbounded or high-cardinality (customers, sites, job numbers, free text, dates). There
 *   is no full set to fetch, and enumerating one would produce thousands of columns.
 */
export type FieldValueSetKind = 'static' | 'bounded-lookup' | 'open';

/**
 * Lookup targets whose full record list is a tenant-configured reference set, not transactional data.
 *
 * These are the settings-style lists an admin maintains: a tenant runs a handful of statuses,
 * priorities, job types and categories, and the count does not grow with business volume. That is what
 * makes it safe to pull the entire set and render one kanban column per entry.
 *
 * People and organisations are deliberately absent — employees, teams and contractors are open, because
 * a tenant may have a hundred or ten thousand of them and nothing in the model bounds which. They make
 * sense as kanban ROWS (data-driven from the loaded page) but never as columns, where the full set is
 * enumerated up front. Customers, sites and jobs are excluded for the same reason.
 */
export const BOUNDED_LOOKUP_ENTITIES: ReadonlySet<string> = new Set<string>([
    ActivityEntityName.WorkOrderStatus,
    ActivityEntityName.JobStatus,
    ActivityEntityName.EntityStatus,
    ActivityEntityName.EntitySubStatus,
    ActivityEntityName.Priority,
    ActivityEntityName.JobType,
    ActivityEntityName.WorkType,
    ActivityEntityName.WorkCategory,
    ActivityEntityName.OpportunityStage,
    ActivityEntityName.AssetType,
    ActivityEntityName.CodeCategory,
    ActivityEntityName.Tag,
]);

/** Field types whose complete option list is carried in metadata. */
const STATIC_VALUE_SET_TYPES: ReadonlySet<MetadataEntityFieldType> = new Set<MetadataEntityFieldType>([
    MetadataEntityFieldType.Enum,
    MetadataEntityFieldType.Flag,
    MetadataEntityFieldType.Boolean,
]);

/**
 * Reads the lookup target entity from a field's `typeData`.
 *
 * `typeData` is either the entity name as a bare string or a `MetadataLookupTypeData` whose `type`
 * holds it — the same two shapes the entity filter field provider reads in its Lookup branch.
 */
export function getLookupTargetEntity(field: MetadataEntityFieldData): string | null {

    if (!field) return null;
    if (typeof field.typeData === 'string') return field.typeData;
    return (field.typeData as MetadataLookupTypeData)?.type ?? null;
}

/**
 * Custom-field classification, or null when the field is not a custom field.
 *
 * Custom dropdowns carry their options in typeData.data; the outer type is the generic CustomField.
 */
function resolveStaticCustomFieldKind(field: MetadataEntityFieldData, type: MetadataEntityFieldType): FieldValueSetKind | null {

    if (type !== MetadataEntityFieldType.CustomField) return null;

    const customType = (field.typeData as any)?.type as CustomFieldType;
    return customType === CustomFieldType.Dropdown || customType === CustomFieldType.DropdownMulti ? 'static' : 'open';
}

/** Classifies how enumerable a field's value set is. */
export function resolveFieldValueSetKind(field: MetadataEntityFieldData): FieldValueSetKind {

    if (!field) return 'open';

    const type = getMetadataEntityFieldType(field);

    // An explicit override beats derivation — it is the escape hatch for the cases the type cannot
    // see (a small custom lookup opting in, a reference list that grew unbounded opting out).
    if (field.valueSet === 'open') return 'open';

    if (field.valueSet === 'bounded') {
        return STATIC_VALUE_SET_TYPES.has(type) || resolveStaticCustomFieldKind(field, type) === 'static' ? 'static' : 'bounded-lookup';
    }

    if (STATIC_VALUE_SET_TYPES.has(type)) return 'static';

    const customKind = resolveStaticCustomFieldKind(field, type);
    if (customKind) return customKind;

    if (type === MetadataEntityFieldType.Lookup || type === MetadataEntityFieldType.Parent || type === MetadataEntityFieldType.Tag) {
        const target = getLookupTargetEntity(field);
        return target && BOUNDED_LOOKUP_ENTITIES.has(target) ? 'bounded-lookup' : 'open';
    }

    return 'open';
}

/** True when a UI may enumerate this field's complete value set (kanban columns, value-set pickers). */
export function isBoundedValueSetField(field: MetadataEntityFieldData): boolean {

    return resolveFieldValueSetKind(field) !== 'open';
}
